import { memo } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import type { Child } from "@/types";
import { formatMoney, getProgress } from "@/lib/format";
import { getChildDisplay } from "@/lib/childDisplay";
import { Card, Tag, ProgressBar } from "@/components/kit";
import { CARD_PHOTO } from "@/components/kit";
import { IGNORE_FOND_CHILD_IDS } from "./ChildDetailPage";

interface ChildCardProps {
  child: Child;
  index: number;
  onClick: (child: Child) => void;
  interactive?: boolean;
}

/** Meta nuqta ajratuvchisi (Figma §5.4 — 4px dot). */
function MetaDot() {
  return <span className="size-1 shrink-0 rounded-full" style={{ background: "var(--c-text-muted)" }} aria-hidden />;
}

function ChildCardComponent({ child, index, onClick, interactive = true }: ChildCardProps) {
  const { t } = useTranslation();
  const d = getChildDisplay(child);

  const showFond = d.fondName && !IGNORE_FOND_CHILD_IDS.includes(child.id);

  const progress = getProgress(d.collected, d.goal);
  const categoryLabel = t(`category${d.category.charAt(0).toUpperCase() + d.category.slice(1)}`);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: Math.min(index, 8) * 0.04 }}
    >
      <Card
        onClick={() => interactive && onClick(child)}
        role={interactive ? "button" : undefined}
        tabIndex={interactive ? 0 : -1}
        onKeyDown={(e) => {
          if (!interactive) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick(child);
          }
        }}
        className="p-3 pb-4 transition-transform active:scale-[0.99]"
        style={{ cursor: interactive ? "pointer" : "default" }}
      >
        {/* Top row: photo + info */}
        <div className="flex gap-3">
          <div
            className="relative shrink-0 overflow-hidden"
            style={{ width: CARD_PHOTO.width, height: CARD_PHOTO.height, borderRadius: CARD_PHOTO.radius, background: "var(--c-line)" }}
          >
            {d.image ? (
              <img
                src={d.image}
                alt={d.name}
                className="absolute inset-0 size-full object-cover object-top"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : null}
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <h3 className="line-clamp-2 text-base font-semibold leading-tight" style={{ color: "var(--c-text)" }}>
              {d.name}
            </h3>

            <div className="flex items-center gap-1.5 text-xs font-medium" style={{ color: "var(--c-text-muted)" }}>
              <span className="whitespace-nowrap">
                {d.age} {t("yearsOld")}
              </span>
              {d.location && (
                <>
                  <MetaDot />
                  <span className="truncate">{d.location}</span>
                </>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-1">
              {d.urgent && <Tag tone="emergency">{t("statUrgent")}</Tag>}
              <Tag tone="neutral">{categoryLabel}</Tag>
            </div>

            {d.fondName && showFond && (
              <div className="mt-0.5 flex items-center gap-2">
                <span
                  className="grid size-8 shrink-0 place-items-center overflow-hidden rounded-full"
                  style={{ boxShadow: "inset 0 0 0 1px var(--c-line)", background: "var(--c-surface)" }}
                >
                  {d.fondIcon ? (
                    <img src={d.fondIcon} alt="" className="size-full object-cover" loading="lazy" />
                  ) : (
                    <span className="text-[10px]" >F</span>
                  )}
                </span>
                <span className="truncate text-xs font-medium" >
                  <span className="truncate" style={{ color: "var(--c-text-muted)" }}>
                    {t("fund")}</span> : {d.fondName}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Diagnosis (single line) */}
        {d.diagnosis && (
          <p className="mt-3 line-clamp-1 text-sm" style={{ color: "var(--c-text)" }}>
            {d.diagnosis}
          </p>
        )}

        {/* Progress */}
        <div className="mt-3">
          <div className="flex items-center gap-2">
            <ProgressBar value={progress} className="flex-1" />
            <span className="text-xs font-medium" style={{ color: "var(--c-text-muted)" }}>
              {progress}%
            </span>
          </div>
          <div className="mt-2 flex items-center justify-between text-xs font-medium">
            {(child.statusId != 4 && child.statusId != 7) &&
              <span style={{ color: "var(--c-text-muted)" }}>
                {t("goal")}: {" "}
                <span style={{ color: "var(--c-text)" }}>
                  {formatMoney(d.goal)}
                </span>
              </span>
            }
            <span style={{ color: "var(--c-text-muted)" }}>
              {t("cardCollected")}:{" "}
              <span style={{ color: "var(--c-text)" }}>
                {formatMoney(d.collected)}

              </span>
            </span>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

export const ChildCard = memo(ChildCardComponent);
